'use client';

import React, { useMemo, useState } from 'react';
import { Package, TrendingUp, ShoppingBag, BarChart3 } from 'lucide-react';

import { MetricCard } from './MetricCard';
import { ChartContainer, type AnalyticsDataPoint } from './ChartContainer';
import { aggregateByGranularity } from '../utils/aggregateByGranularity';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';

type TopNOption = 5 | 10 | 20 | 50 | 'all';

const TOP_N_OPTIONS: { value: TopNOption; label: string }[] = [
    { value: 5, label: 'Top 5' },
    { value: 10, label: 'Top 10' },
    { value: 20, label: 'Top 20' },
    { value: 50, label: 'Top 50' },
    { value: 'all', label: 'All' },
];

function TopNSelector({ value, onChange }: { value: TopNOption; onChange: (value: TopNOption) => void }) {
    const { t } = useTranslation('analytics');
    return (
        <Select
            value={String(value)}
            onValueChange={(v) => onChange(v === 'all' ? 'all' : Number(v) as TopNOption)}
        >
            <SelectTrigger className="h-7 w-[90px] text-xs">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {TOP_N_OPTIONS.map((option) => (
                    <SelectItem key={String(option.value)} value={String(option.value)}>
                        {option.value === 'all' ? t('products.all') : t('products.topN', { count: option.value })}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

interface ProductMetricsV4Props {
    data: {
        productsByRevenue?: { productName: string; amount: number; count: number }[];
        variantsByRevenue?: { variantName: string; amount: number; count: number }[];
        topSellingProducts?: { productName: string; quantity: number }[];
        productSalesOverTime?: Map<string, { quantity: number }>;
    } | null | undefined;
    granularity: 'daily' | 'weekly' | 'monthly';
    currency: string;
    loadingStates: {
        productsByRevenue: boolean;
        variantsByRevenue: boolean;
        topSellingProducts: boolean;
        productSalesOverTime: boolean;
    };
    error: any;
}

interface MetricSummaryProps {
    title: string;
    value: string | number;
    icon: React.ComponentType<{ className?: string }>;
    subtitle?: string;
    isLoading?: boolean;
}

function MetricSummary({ title, value, icon: Icon, subtitle, isLoading }: MetricSummaryProps) {
    return (
        <div className="flex items-center justify-between p-4 bg-card border rounded-lg shadow-sm">
            <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <p className="text-sm font-medium text-muted-foreground">{title}</p>
                    {isLoading ? (
                        <div className="space-y-2 mt-2">
                            <Skeleton className="h-7 w-32" />
                            <Skeleton className="h-3 w-24" />
                        </div>
                    ) : (
                        <>
                            <p className="text-lg font-semibold text-foreground">{value}</p>
                            {subtitle && (
                                <p className="text-xs text-muted-foreground">{subtitle}</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export function ProductMetricsV4({ data, granularity, currency, loadingStates, error }: ProductMetricsV4Props) {
    const { t } = useTranslation('analytics');
    const [productsByRevenueTopN, setProductsByRevenueTopN] = useState<TopNOption>(10);
    const [variantsByRevenueTopN, setVariantsByRevenueTopN] = useState<TopNOption>(10);
    const [topSellingProductsTopN, setTopSellingProductsTopN] = useState<TopNOption>(10);

    // Transform productsByRevenue to AnalyticsDataPoint format with Top N filter
    const productsByRevenueData = useMemo((): AnalyticsDataPoint[] => {
        if (!data?.productsByRevenue) return [];
        const allData = data.productsByRevenue.map(item => ({
            code: item.productName,
            value: item.amount,
            volume: item.count,
            currency,
        }));
        return productsByRevenueTopN === 'all' ? allData : allData.slice(0, productsByRevenueTopN);
    }, [data?.productsByRevenue, currency, productsByRevenueTopN]);

    // Transform variantsByRevenue to AnalyticsDataPoint format with Top N filter
    const variantsByRevenueData = useMemo((): AnalyticsDataPoint[] => {
        if (!data?.variantsByRevenue) return [];
        const allData = data.variantsByRevenue.map(item => ({
            code: item.variantName,
            value: item.amount,
            volume: item.count,
            currency,
        }));
        return variantsByRevenueTopN === 'all' ? allData : allData.slice(0, variantsByRevenueTopN);
    }, [data?.variantsByRevenue, currency, variantsByRevenueTopN]);

    // Transform topSellingProducts to AnalyticsDataPoint format with Top N filter
    const topSellingProductsData = useMemo((): AnalyticsDataPoint[] => {
        if (!data?.topSellingProducts) return [];
        const allData = data.topSellingProducts.map(item => ({
            code: item.productName,
            value: item.quantity,
        }));
        return topSellingProductsTopN === 'all' ? allData : allData.slice(0, topSellingProductsTopN);
    }, [data?.topSellingProducts, topSellingProductsTopN]);

    // Transform productSalesOverTime to AnalyticsDataPoint format with granularity
    const productSalesOverTimeData = useMemo((): AnalyticsDataPoint[] => {
        if (!data?.productSalesOverTime) return [];
        const rawData = Array.from(data.productSalesOverTime.entries())
            .map(([code, { quantity }]) => ({
                code,
                value: quantity,
            }))
            .sort((a, b) => new Date(a.code).getTime() - new Date(b.code).getTime());
        return aggregateByGranularity(rawData, granularity);
    }, [data?.productSalesOverTime, granularity]);

    // Calculate summary metrics
    const totalProductsSold = useMemo(() => {
        if (!data?.topSellingProducts) return 0;
        return data.topSellingProducts.reduce((sum, item) => sum + item.quantity, 0);
    }, [data?.topSellingProducts]);

    const uniqueProductsCount = useMemo(() => {
        if (!data?.productsByRevenue) return 0;
        return data.productsByRevenue.length;
    }, [data?.productsByRevenue]);

    const uniqueVariantsCount = useMemo(() => {
        if (!data?.variantsByRevenue) return 0;
        return data.variantsByRevenue.length;
    }, [data?.variantsByRevenue]);

    if (error) {
        return (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <MetricCard title={t('products.errorTitle')} description={t('products.errorDescription')} className="col-span-full">
                    <div className="flex items-center justify-center h-full">
                        <p className="text-destructive">{t('products.errorMessage')}</p>
                    </div>
                </MetricCard>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <MetricSummary
                    title={t('products.totalProductsSold')}
                    value={totalProductsSold.toLocaleString()}
                    icon={ShoppingBag}
                    subtitle={t('products.units')}
                    isLoading={loadingStates.topSellingProducts}
                />
                <MetricSummary
                    title={t('products.uniqueProducts')}
                    value={uniqueProductsCount}
                    icon={Package}
                    subtitle={t('products.withSales')}
                    isLoading={loadingStates.productsByRevenue}
                />
                <MetricSummary
                    title={t('products.uniqueVariants')}
                    value={uniqueVariantsCount}
                    icon={BarChart3}
                    subtitle={t('products.withSales')}
                    isLoading={loadingStates.variantsByRevenue}
                />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <MetricCard
                    title={t('products.topByRevenue')}
                    description={t('products.topByRevenueDescription')}
                    isLoading={loadingStates.productsByRevenue}
                    headerAction={
                        <TopNSelector
                            value={productsByRevenueTopN}
                            onChange={setProductsByRevenueTopN}
                        />
                    }
                >
                    <ChartContainer
                        data={productsByRevenueData}
                        chartType="bar"
                        currency={currency}
                        emptyStateMessage={t('products.noProductRevenue')}
                    />
                </MetricCard>

                <MetricCard
                    title={t('products.topVariantsByRevenue')}
                    description={t('products.topVariantsDescription')}
                    isLoading={loadingStates.variantsByRevenue}
                    headerAction={
                        <TopNSelector
                            value={variantsByRevenueTopN}
                            onChange={setVariantsByRevenueTopN}
                        />
                    }
                >
                    <ChartContainer
                        data={variantsByRevenueData}
                        chartType="bar"
                        currency={currency}
                        emptyStateMessage={t('products.noVariantRevenue')}
                    />
                </MetricCard>

                <MetricCard
                    title={t('products.topSelling')}
                    description={t('products.topSellingDescription')}
                    isLoading={loadingStates.topSellingProducts}
                    headerAction={
                        <TopNSelector
                            value={topSellingProductsTopN}
                            onChange={setTopSellingProductsTopN}
                        />
                    }
                >
                    <ChartContainer
                        data={topSellingProductsData}
                        chartType="bar"
                        emptyStateMessage={t('products.noProductSales')}
                    />
                </MetricCard>

                <MetricCard
                    title={t('products.salesOverTime')}
                    description={t('products.salesOverTimeDescription')}
                    isLoading={loadingStates.productSalesOverTime}
                >
                    <ChartContainer
                        data={productSalesOverTimeData}
                        chartType="line"
                        emptyStateMessage={t('products.noSalesOverTime')}
                    />
                </MetricCard>
            </div>
        </div>
    );
}
